import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { POPUP_COOLDOWN_MS } from '../config/siteConfig';

const PopupContext = createContext(null);

export const usePopupManager = () => {
  const ctx = useContext(PopupContext);
  if (!ctx) throw new Error('usePopupManager must be used within PopupProvider');
  return ctx;
};

const isInCooldown = () => {
  const lastClosed = parseInt(sessionStorage.getItem('lastPopupClosedAt') || '0', 10);
  return Date.now() - lastClosed < POPUP_COOLDOWN_MS;
};

export const PopupProvider = ({ children }) => {
  const [activePopup, setActivePopup] = useState(null);
  const activeRef = useRef(null);
  activeRef.current = activePopup;

  const tryRequestPopup = useCallback((id) => {
    if (activeRef.current) return false;
    if (isInCooldown()) return false;
    setActivePopup(id);
    return true;
  }, []);

  const requestPopup = useCallback((id) => {
    tryRequestPopup(id);
  }, [tryRequestPopup]);

  const closePopup = useCallback((id) => {
    setActivePopup((current) => {
      if (current === id) {
        sessionStorage.setItem('lastPopupClosedAt', String(Date.now()));
        return null;
      }
      return current;
    });
  }, []);

  const isActive = useCallback(
    (id) => activePopup === id,
    [activePopup]
  );

  const canRequestPopup = useCallback(() => {
    return !activeRef.current && !isInCooldown();
  }, []);

  return (
    <PopupContext.Provider value={{ activePopup, requestPopup, tryRequestPopup, closePopup, isActive, canRequestPopup }}>
      {children}
    </PopupContext.Provider>
  );
};

export default PopupProvider;
